import type { Me } from '@/shared/api/session'
import { visibleAdminNav } from '@/shared/admin-access'
import type { AdminDest } from '@/shared/routes'
import { initials } from '@/shared/lib/contact'
import { Ico } from '@/ui/Ico'
import { cls } from '@/ui/cls'
import { useBrand } from '@/shared/white-label/useBrand'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface Props {
  me: Me | undefined
  /** True when an admin view is currently showing — highlights the matching item. */
  adminActive: boolean
  /** Active admin destination, used to mark the current section. */
  adminDest: AdminDest | null
  /** Navigate to an admin destination. */
  onOpenAdmin: (dest: AdminDest) => void
}

// On Radix DropdownMenu: role=menu/menuitem, Escape, outside click and focus
// trap/return come from the primitive, and selecting an item closes the menu
// on its own — the open/outside-click/Escape wiring this used to hand-roll
// is gone.
export default function ProfileMenu({ me, adminActive, adminDest, onOpenAdmin }: Props) {
  const { person, anonymised } = useBrand()
  const adminSections = visibleAdminNav(me)
  const displayName = me?.name || me?.email || ''

  const signOut = () => window.location.assign('/auth/logout')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={me?.email}
          aria-label={displayName ? `Kontomeny — ${displayName}` : 'Kontomeny'}
          className={cls(
            'group inline-flex items-center gap-1.5 h-[30px] pl-1 pr-1.5 border cursor-pointer rounded-full',
            'transition-colors bg-transparent border-transparent hover:bg-ink-50',
            'data-[state=open]:bg-ink-50 data-[state=open]:border-ink-200',
          )}
        >
          <Avatar size="sm">
            <AvatarFallback className="bg-brand text-ink-1000">{initials(displayName)}</AvatarFallback>
          </Avatar>
          <span className="hidden sm:block max-w-[120px] truncate text-xs font-medium text-ink-900">
            {displayName}
          </span>
          <span
            className="inline-flex w-3 h-3 text-ink-500 transition-transform [&_svg]:w-3 [&_svg]:h-3 group-data-[state=open]:rotate-180"
            aria-hidden="true"
          >
            {Ico.chevronDown}
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" aria-label="Kontomeny" className="w-60 rounded">
        <div className="px-3 py-2.5 -m-1 mb-1 border-b border-ink-100">
          <div className="text-sm font-medium text-ink-900 truncate">
            {anonymised ? person(me?.email) : displayName}
          </div>
          {me?.email && !anonymised && (
            <div className="text-xs text-ink-500 truncate">{me.email}</div>
          )}
          {me?.role && <div className="text-xs text-ink-500 mt-0.5 capitalize">{me.role}</div>}
        </div>

        {adminSections.length > 0 && (
          <>
            <DropdownMenuLabel>Admin</DropdownMenuLabel>
            {adminSections.map(section => {
              const current = adminActive && adminDest?.section === section.key
              return (
                <DropdownMenuItem
                  key={section.key}
                  onSelect={() => onOpenAdmin({ section: section.key, sub: section.subs[0].key })}
                  className={cls('text-sm', current ? 'text-ink-1000 font-medium' : 'text-ink-700')}
                >
                  {section.label}
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onSelect={signOut} className="text-sm text-ink-700">
          Logga ut
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
